import React from 'react';
import { CheckCircle2, Clock, ReceiptPoundSterling, BadgeCheck, Truck, Package, ShieldCheck } from 'lucide-react';

/**
 * Status timeline for an order/quote. Renders the lifecycle stages with the
 * timestamp of each transition (or "Awaiting…" if not yet reached).
 *
 * Works for ProBound (db.quotes) and ProEpoxy / ProDecor orders — they all
 * use the same field names: submitted_at, invoiced_at, paid_at,
 * payment_confirmed_at, dispatched_at, delivered_at.
 *
 * Pass `compact` to render a single inline line for list rows; otherwise the
 * full vertical timeline is shown.
 *
 * `actions` (full mode only) — optional map of stage-key → React node rendered
 * on the right of that stage row (e.g. a "Mark as invoiced" button).
 */
const STAGES = [
  { key: 'submitted',         label: 'Submitted',          field: ['submitted_at', 'created_at'], icon: Package,              color: 'text-amber-700',   dot: 'bg-amber-500' },
  { key: 'invoiced',          label: 'Invoiced',           field: ['invoiced_at'],                icon: ReceiptPoundSterling, color: 'text-indigo-700',  dot: 'bg-indigo-500' },
  { key: 'paid',              label: 'Customer Paid',      field: ['paid_at'],                    icon: BadgeCheck,           color: 'text-teal-700',    dot: 'bg-teal-500' },
  { key: 'payment_confirmed', label: 'Payment Confirmed',  field: ['payment_confirmed_at'],       icon: ShieldCheck,          color: 'text-blue-700',    dot: 'bg-blue-500' },
  { key: 'dispatched',        label: 'Dispatched',         field: ['dispatched_at'],              icon: Truck,                color: 'text-purple-700',  dot: 'bg-purple-500' },
  { key: 'delivered',         label: 'Delivered',          field: ['delivered_at'],               icon: CheckCircle2,         color: 'text-emerald-700', dot: 'bg-emerald-500' },
];

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return null;
  }
};

const pickTimestamp = (order, fields) => {
  for (const f of fields) {
    if (order?.[f]) return order[f];
  }
  return null;
};

export function OrderStageTimeline({ order, compact = false, hideUnreached = false, actions = {} }) {
  if (!order) return null;

  const rows = STAGES
    .map((s) => ({ ...s, at: fmtDateTime(pickTimestamp(order, s.field)) }))
    .filter((s) => !hideUnreached || s.at);

  if (compact) {
    const reached = rows.filter((s) => s.at);
    if (reached.length === 0) return null;
    return (
      <div
        data-testid="order-stage-timeline-compact"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600"
      >
        {reached.map((s, i) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
            <span className={`font-semibold ${s.color}`}>{s.label}</span>
            <span className="text-gray-500">{s.at}</span>
            {i < reached.length - 1 && <span className="text-gray-300">·</span>}
          </span>
        ))}
      </div>
    );
  }

  return (
    <ol
      data-testid="order-stage-timeline"
      className="space-y-3"
    >
      {rows.map((s) => {
        const Icon = s.icon;
        const reached = !!s.at;
        const extra = s.key === 'invoiced' ? actions.raise_invoice : null;  // "Raise invoice in Xero" sits beside the Invoiced stage
        const action = actions[s.key] || extra ? <div className="flex flex-wrap gap-2 justify-end">{extra}{actions[s.key]}</div> : null;
        return (
          <li
            key={s.key}
            data-testid={`order-stage-${s.key}`}
            className="flex items-start gap-3"
          >
            <span
              className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${
                reached ? `${s.dot} text-white` : 'bg-gray-200 text-gray-400'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-bold ${reached ? s.color : 'text-gray-400'}`}>
                {s.label}
              </div>
              <div className={`text-xs ${reached ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                {reached ? s.at : (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Awaiting…
                  </span>
                )}
              </div>
            </div>
            {action && (
              <div className="shrink-0 self-center" data-testid={`order-stage-${s.key}-action`}>
                {action}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default OrderStageTimeline;
